import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { Link, useHistory, useParams } from 'react-router-dom';
import CalculationInvoiceForm from '../../components/CalculationInvoiceForm';
import Page from '@afosto/app-components-react/Page';
import SkeletonPage from '@afosto/app-components-react/SkeletonPage';
import fetchMultipleProducts from '@afosto/utils-react/fetchMultipleProducts';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import uuid from '@afosto/utils/uuid';
import { calculations as calculationsApi } from '@afosto/order/api/Calculations';
import translations from './translations';

const CalculationInvoice = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:invoices:read']);

  const history = useHistory();
  const intl = useIntl();
  const { calculationID } = useParams();
  const [calculation, setCalculation] = useState(null);
  const [calculationItems, setCalculationItems] = useState([]);
  const [isFetchingCalculation, setIsFetchingCalculation] = useState(true);

  useEffect(() => {
    const fetchCalculation = async id => {
      try {
        const response = await calculationsApi.getCalculation(id);
        const { data = null } = response;

        const skus = data.items.map(item => item.sku);
        const products = await fetchMultipleProducts(skus);

        const formattedItems = (data?.items ?? []).reduce((acc, item) => {
          const product = findObjectInArray(products, 'sku', item.sku);
          const generatedID = uuid();
          const availableIds = (item.order_items ?? [])
            .filter(orderItem => orderItem.is_invoiced === false)
            .map(orderItem => orderItem.id);

          return [
            ...acc,
            {
              id: generatedID,
              reference: generatedID,
              sku: item.sku,
              description: item.description,
              amount: item.amount,
              isDiscounted: item.is_discounted,
              quantity: item.quantity > 0 ? availableIds.length : availableIds.length * -1,
              subtotal: item.subtotal,
              adjustments: item.adjustments,
              total: item.total,
              ids: availableIds,
              image: product?.image,
              vat_percentage: item.vat_percentage,
            },
          ];
        }, []);

        setCalculation(data ?? null);
        setCalculationItems(formattedItems);
      } catch (err) {
        setCalculation(null);
        setCalculationItems([]);
      } finally {
        setIsFetchingCalculation(false);
      }
    };

    if (calculationID) {
      fetchCalculation(calculationID).catch(() => {
        // Catch only.
      });
    }
  }, [calculationID]);

  const handleCreateInvoice = async ({ items = [] }) => {
    try {
      const itemIds = items.reduce((acc, item) => {
        const ids = (item?.ids ?? []).slice(0, Math.abs(item?.quantity));
        return [...acc, ...ids];
      }, []);

      const response = await calculationsApi.createInvoiceForCalculation(calculation?.id, {
        order_item_ids: itemIds,
      });
      const { data } = response;

      history.push(`/invoices/${data?.id}`);
    } catch (err) {
      // Do something.
    }
  };

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)}>
        <UnauthorizedComponent />
      </Page>
    );
  }

  if (isFetchingCalculation) {
    return <SkeletonPage fullWidth>Loading</SkeletonPage>;
  }

  if (!calculation) {
    return 'Calculation not found!';
  }

  return (
    <Page
      title={intl.formatMessage(translations.title)}
      breadcrumbs={[
        { label: calculation?.number ?? 'Concept', path: `/calculations/${calculationID}` },
        {
          label: intl.formatMessage(translations.title),
          path: `/calculations/${calculationID}/invoice`,
          active: true,
        },
      ]}
      linkComponent={Link}
    >
      <CalculationInvoiceForm
        calculation={calculation}
        items={calculationItems}
        onCancel={() => history.push(`/calculations/${calculation?.id}`)}
        onSubmit={handleCreateInvoice}
      />
    </Page>
  );
};

export default CalculationInvoice;
